'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { TypeImage } from '@/core/generated-types';

import { CTA_NAMES, PAGE_LOCATIONS } from '@/utils/rudderstack';

import { Item } from './components/Item';
import AddChild from './components/ChildCompatible/AddChild';

export type TypeItem = {
  id: string;
  title: string;
  icon: TypeImage;
};

type CommonProps = {
  items: TypeItem[];
  selectedItemId: string;
  setSelectedItemId: (itemId: string) => void;
  trackCtaName: CTA_NAMES;
  trackPageLocation: PAGE_LOCATIONS;
};

type ChildSpecificProps = {
  hasEditChild?: boolean;
  editChildText?: string;
  hasAddChild?: boolean;
  addChildTitle?: string;
};

export type ItemSelectorProps = CommonProps & ChildSpecificProps;

const ItemSelector: FC<ItemSelectorProps> = ({
  // Common props
  items,
  selectedItemId,
  setSelectedItemId,
  trackCtaName,
  trackPageLocation,
  // Child specific props
  hasEditChild = false,
  editChildText = '',
  hasAddChild = false,
  addChildTitle = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  // used to toggle between justify-center and justify-left (necessary for large quantities of items)
  const checkOverflow = useCallback(() => {
    const container = containerRef.current;

    if (container) {
      setIsOverflowing(container.scrollWidth > container.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  return (
    <div
      data-testid="item-selector"
      ref={containerRef}
      className={classNames(
        'w-full pt-10 pb-4 lg:pb-8 flex flex-row flex-nowrap  space-x-2 lg:space-x-6 overflow-x-auto scroll-smooth',
        {
          'justify-start': isOverflowing,
          'justify-center': !isOverflowing,
        },
      )}
    >
      {items.map((item) => (
        <Item
          key={item.id}
          item={item}
          setSelectedItemId={setSelectedItemId}
          isSelected={selectedItemId === item.id}
          trackCtaName={trackCtaName}
          trackPageLocation={trackPageLocation}
          hasEditChild={hasEditChild}
          editChildText={editChildText}
        />
      ))}

      {hasAddChild && <AddChild title={addChildTitle} />}
    </div>
  );
};

export default ItemSelector;
